import { SERVER_URL } from '../DB_Url';

const ORG_API_TIMEOUT_MS = 10000;

const parseJsonSafely = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return null;
  }
  try {
    return await response.json();
  } catch {
    return null;
  }
};

class OrgApi {
  constructor() {
    this.baseUrl = SERVER_URL;
  }

  async request(path, { method = 'POST', body, headers = {} } = {}) {
    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(() => controller.abort(), ORG_API_TIMEOUT_MS);

    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      const data = await parseJsonSafely(response);

      if (!response.ok) {
        throw new Error(data?.error || `Org request failed with status ${response.status}.`);
      }

      return data || {};
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw new Error('Org request timed out.');
      }
      throw error;
    } finally {
      globalThis.clearTimeout(timeoutId);
    }
  }

  createOrganization({ idToken, organization, contractorType, trade, companySize, country, website }) {
    const body = { organization };
    if (contractorType !== undefined) body.contractorType = contractorType;
    if (trade !== undefined) body.trade = trade;
    if (companySize !== undefined) body.companySize = companySize;
    if (country !== undefined) body.country = country;
    if (website !== undefined) body.website = website;
    return this.request('/api/auth/org/create', {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
      body,
    });
  }

  updateOrganization({ idToken, organization, contractorType, trade, companySize, country, website }) {
    const body = { organization };
    if (contractorType !== undefined) body.contractorType = contractorType;
    if (trade !== undefined) body.trade = trade;
    if (companySize !== undefined) body.companySize = companySize;
    if (country !== undefined) body.country = country;
    if (website !== undefined) body.website = website;
    return this.request('/api/auth/org/update', {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
      body,
    });
  }

  getOrgInfo({ idToken, organization }) {
    const qs = encodeURIComponent(organization);
    return this.request(`/api/auth/org/info?organization=${qs}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    }).then((data) => data?.org ?? null);
  }

  inviteMember({ idToken, organization, email, role }) {
    return this.request('/api/auth/org/invite', {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
      body: { organization, email, role },
    });
  }

  getOrgMembers({ idToken, organization }) {
    const qs = encodeURIComponent(organization);
    return this.request(`/api/auth/org/members?organization=${qs}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });
  }

  deleteOrganization({ idToken, organization }) {
    return this.request('/api/auth/org/delete', {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
      body: { organization },
    });
  }

  respondToInvite({ idToken, inviteId, accept }) {
    return this.request('/api/auth/org/invite/respond', {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
      body: { inviteId, accept },
    });
  }

  async getInviteByToken({ idToken, inviteId }) {
    const qs = encodeURIComponent(inviteId);
    try {
      const data = await this.request(`/api/auth/org/invites/lookup?inviteId=${qs}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      return data?.invite || null;
    } catch (error) {
      if (error?.message && /404|not found/i.test(error.message)) {
        return null;
      }
      throw error;
    }
  }

  async getMyInvites({ idToken }) {
    const data = await this.request('/api/auth/org/invites', {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return data.invites || [];
  }

  async getOrgInviteStatus({ idToken, organization }) {
    const qs = encodeURIComponent(organization);
    const data = await this.request(`/api/auth/org/invites/status?organization=${qs}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return data.invites || [];
  }
}

const orgApi = new OrgApi();

export default orgApi;
